  import React, { useEffect, useMemo, useState } from "react";
  import styled from "styled-components";
  import { Info } from "lucide-react";
  import { FaFileExcel } from "react-icons/fa";
  import { Tooltip as InfoTooltip } from "react-tooltip";

  // ====== styles (matches your table vibe) ======
  const TableWrapper = styled.div`
    background-color: #f9f9f9;
    width: 100%;
    padding: 20px;
  `;

  const TitleWrapper = styled.div`
    font-family: Arial, sans-serif;
    font-size: 12px;
    font-weight: bold;
    color: #666;
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  `;

  const ExportButton = styled.button`
    display: flex;
    align-items: center;
    padding: 8px 12px;
    background-color: #1d6f42;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    &:hover { background-color: #155e36; }
    svg { margin-right: 6px; }
  `;

  const ControlsRow = styled.div`
    display: flex;
    justify-content: center;
    gap: 15px;
    margin-bottom: 14px;
    flex-wrap: wrap;
  `;

  const Input = styled.input`
    padding: 6px 10px;
    border-radius: 6px;
    font-size: 14px;
    border: 1px solid #ccc;
    width: 320px;
  `;

  const ScrollContainer = styled.div`
    max-height: 720px;
    overflow-y: auto;
  `;

  const StyledTable = styled.table`
    border-collapse: collapse;
    width: 100%;
    font-size: 0.7rem;

    th, td {
      border: 1px solid #ddd;
      text-align: center;
      padding: 12px 8px;
    }

    th:nth-child(1), td:nth-child(1) { width: 40%; }
    th:nth-child(2), td:nth-child(2) { width: 18%; }
    th:nth-child(3), td:nth-child(3) { width: 22%; }
    th:nth-child(4), td:nth-child(4) { width: 20%; }

    th {
      background: #87ceeb;
      position: sticky;
      top: 0;
      z-index: 2;
    }

    tr:nth-child(even) { background-color: #f9f9f9; }
    tr:hover { background-color: #eef6ff; }
  `;

  // ====== helpers ======
  const ymKey = (y, m) => `${y}-${String(m).padStart(2, "0")}`;

  const prevMonthKey = (key, n = 1) => {
    const [y, m] = key.split("-").map(Number);
    const d = new Date(y, m - 1, 1);
    d.setMonth(d.getMonth() - n);
    return ymKey(d.getFullYear(), d.getMonth() + 1);
  };

  // Positive-only value for “purchase” decision
  const parseVal = (row) =>
    parseFloat(
      row.K_15 || row.K_19 || row.K_10 || row.K_21 || row.K_17 || row.K_11 || row.K_22
    ) || 0;

  const getPositive = (v) => (v > 0 ? v : 0);

  const safeDate = (val) => {
    if (!val) return null;
    const d = new Date(val);
    return isNaN(d) ? null : d;
  };

  const formatPLN = (v) =>
    Number(v || 0).toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // ====== component ======
  const ChurnedLoyalCustomersTable = ({ salesData = [] }) => {
    const [rows, setRows] = useState([]);
    const [filterText, setFilterText] = useState("");

    // latest month in dataset (by Rok/Miesiac, regardless of sign)
    const latestKey = useMemo(() => {
      if (!salesData.length) return null;
      return salesData.reduce((latest, r) => {
        const y = parseInt(r.Rok, 10);
        const m = parseInt(r.Miesiac, 10);
        if (isNaN(y) || isNaN(m)) return latest;
        const k = ymKey(y, m);
        return !latest || latest < k ? k : latest;
      }, null);
    }, [salesData]);

    useEffect(() => {
      if (!salesData.length || !latestKey) {
        setRows([]);
        return;
      }

      // Months: m3, m2, m1 must have positive purchases; m0 must have none.
      const m0 = latestKey;           // current month (no buy)
      const m1 = prevMonthKey(m0, 1); // -1
      const m2 = prevMonthKey(m0, 2); // -2
      const m3 = prevMonthKey(m0, 3); // -3
      const last3Set = new Set([m1, m2, m3]);

      // Aggregate positive sums per customer per month
      // key: "name||nip" -> { [ym]: { posSum: number } }
      const perCustMonth = {};
      // Track last positive purchase date within the last-3 window
      const lastPosDateInLast3 = {};

      for (const row of salesData) {
        const name = row.NazwaKontrahenta || "Nieznany Kontrahent";
        const rawNip = (row.NrKontrahenta ?? "").toString().trim();
        const nip = rawNip || "—";
        const y = parseInt(row.Rok, 10);
        const m = parseInt(row.Miesiac, 10);
        if (isNaN(y) || isNaN(m)) continue;

        const k = ymKey(y, m);
        const key = `${name}||${nip}`;
        const val = parseVal(row);

        if (!perCustMonth[key]) perCustMonth[key] = {};
        if (!perCustMonth[key][k]) perCustMonth[key][k] = { posSum: 0 };

        // Only positive values count toward "purchased this month"
        if (val > 0) {
          perCustMonth[key][k].posSum += val;

          // If this month is in the last-3 window, update last positive date
          if (last3Set.has(k)) {
            const d = safeDate(row.DataWystawienia);
            if (d) {
              const curr = lastPosDateInLast3[key];
              if (!curr || d > curr) lastPosDateInLast3[key] = d;
            }
          }
        }
      }

      // Build result rows:
      // Require posSum > 0 for m3, m2, m1; and posSum == 0 (or undefined) for m0.
      const result = [];
      for (const key of Object.keys(perCustMonth)) {
        const [name, nip] = key.split("||");
        const p1 = perCustMonth[key][m1]?.posSum || 0;
        const p2 = perCustMonth[key][m2]?.posSum || 0;
        const p3 = perCustMonth[key][m3]?.posSum || 0;
        const p0 = perCustMonth[key][m0]?.posSum || 0; // current

        if (p1 > 0 && p2 > 0 && p3 > 0 && p0 === 0) {
          const sumLast3 = p1 + p2 + p3;
          const lastDate =
            lastPosDateInLast3[key] ? lastPosDateInLast3[key].toLocaleDateString("pl-PL") : "—";

          result.push({
            customer: name,
            nip,
            valueLast3: sumLast3,
            lastDateBought: lastDate,
          });
        }
      }

      // Sort by valueLast3 DESC, then customer ASC
      result.sort((a, b) => b.valueLast3 - a.valueLast3 || a.customer.localeCompare(b.customer));
      setRows(result);
    }, [salesData, latestKey]);

    const filtered = rows.filter((r) => {
      const q = filterText.trim().toLowerCase();
      if (!q) return true;
      return r.customer.toLowerCase().includes(q) || (r.nip || "").toLowerCase().includes(q);
    });

    const exportCSV = () => {
      const headers = ["Kontrahent", "NIP", "Wartość z ostatnich 3 mies. (PLN)", "Data ostatniego zakupu"];
      const body = filtered.map((r) => [
        r.customer,
        r.nip || "",
        formatPLN(r.valueLast3),
        r.lastDateBought,
      ]);

      const sep = ";";
      const esc = (s) => {
        const v = String(s ?? "");
        return /[;"\n\r]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
      };
      const csv = [headers, ...body].map((row) => row.map(esc).join(sep)).join("\r\n");
      const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });

      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `stali_klienci_ktorzy_odeszli.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    };

    return (
      <TableWrapper>
        <TitleWrapper>
          Stali klienci, którzy odeszli
          <div data-tooltip-id="churned-tooltip" style={{ cursor: "pointer" }}>
            <Info size={18} />
          </div>
        </TitleWrapper>

        <InfoTooltip
          id="churned-tooltip"
          place="right"
          style={{ fontSize: "12px", fontFamily: "Arial, sans-serif", color: "#fff" }}
          content={
            <>
              <strong>Stali klienci, którzy odeszli</strong><br/>
              Klienci kupowali <b>3 miesiące z rzędu</b>, a w bieżącym (4.) miesiącu <b>nie zrobili zakupu</b>.<br/>
              (Korekty/ujemne kwoty nie liczą się jako zakup).
            </>
          }
        />

        <ControlsRow>
          <div>
            <label>Kontrahent / NIP: </label>
            <Input
              type="text"
              placeholder="Wpisz nazwę lub NIP…"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
            />
          </div>

          <ExportButton onClick={exportCSV}>
            <FaFileExcel />
            Eksportuj do Excel
          </ExportButton>
        </ControlsRow>

        <ScrollContainer>
          <StyledTable>
            <thead>
              <tr>
                <th>Kontrahent</th>
                <th>NIP</th>
                <th>Wartość z ostatnich 3 mies. (PLN)</th>
                <th>Data ostatniego zakupu</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length ? (
                filtered.map((r, i) => (
                  <tr key={`${r.customer}-${r.nip}-${i}`}>
                    <td>{r.customer}</td>
                    <td>{r.nip}</td>
                    <td>{formatPLN(r.valueLast3)}</td>
                    <td>{r.lastDateBought}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4">Brak klientów spełniających kryteria.</td>
                </tr>
              )}
            </tbody>
          </StyledTable>
        </ScrollContainer>
      </TableWrapper>
    );
  };

  export default ChurnedLoyalCustomersTable;
